/*  @typescript-eslint/restrict-template-expressions */

/**
 * All authentication methods are available here.
 */

/**
 * TODO: Figure out how to encrypt the password or hide the hash
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { Axios, AxiosPromise, AxiosResponse } from "axios";
import {
  IConfirmCode,
  IEmail,
  ILogin,
  IResetPassword,
  ISignUpUser,
} from "Interfaces";
import { url } from "inspector";
import { queryKeys } from "queries";
import { axios } from "utils";
// import crypto from "crypto-js";

/**
 * Testing with the following hash
 */

const SECRET_HASH =
  "c08d799fb438d1324db01ea149023ccd9156ce9b9bcfcb765ffe25ce3d758507";

const Login = async (payload: ILogin): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/login",
    method: "POST",
    data: payload,
  });
};
const AddressData = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/update-address-google",
    method: "POST",
    data: payload,
  });
};
const AutoLogin = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/auto-login",
    method: "POST",
    data: payload,
  });
};
export const useAutoLoginQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(AutoLogin, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.GET_USER_PROFILE],
      });

      // call after user authentication
      setTimeout(() => {
        void (async () => {
          await queryClient.invalidateQueries({
            queryKey: [queryKeys.SURVEY_INFO],
          });
        })();
      }, 0);
    },
  });
};
export const useAddressDataQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  unknown
> => {
  return useMutation(AddressData);
};

export const useLoginQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  ILogin,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(Login, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.GET_USER_PROFILE],
      });

      // call after user authentication
      setTimeout(() => {
        void (async () => {
          await queryClient.invalidateQueries({
            queryKey: [queryKeys.SURVEY_INFO],
          });
        })();
      }, 0);
    },
  });
};

const SignUp = async (payload: ISignUpUser): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/register",
    method: "POST",
    data: payload,
  });
};

export const useSignUpQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  ISignUpUser,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(SignUp, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.GET_USER_PROFILE],
      });
    },
  });
};

const ConfirmUser = async (
  payload: IConfirmCode
): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "POST",
    url: "/auth/confirm",
    data: payload,
  });
};

export const useConfirmUserQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  IConfirmCode,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(ConfirmUser, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.GET_USER_PROFILE],
      });
    },
  });
};

const ResendCode = async (payload: IEmail): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/resend-code",
    method: "POST",
    data: payload,
  });
};

export const useResendCodeQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  IEmail,
  unknown
> => {
  return useMutation(ResendCode);
};

const ForgotPassword = async (payload: IEmail): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/forgot-password",
    method: "POST",
    data: payload,
  });
};

export const useForgotPasswordQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  IEmail,
  unknown
> => {
  return useMutation(ForgotPassword);
};

const ResetPassword = async (
  payload: IResetPassword
): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/reset-password",
    method: "POST",
    data: payload,
  });
};

export const useResetPasswordQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  IResetPassword,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(ResetPassword, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.GET_USER_PROFILE],
      });
    },
  });
};

const LogoutUser = async (): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/logout",
    method: "DELETE",
  });
};

export const useLogoutUserQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  void,
  unknown
> => {
  return useMutation(LogoutUser);
};

const UpdateMobileNumber = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/auth/update-profile",
    method: "PATCH",
    data: payload,
  });
};

export const useUpdateMobileNumberQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  any,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(UpdateMobileNumber, {
    onSuccess: async () => {
      await queryClient.invalidateQueries([queryKeys.GET_USER_PROFILE]);
    },
  });
};
